import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useClinicianClinics, useCreatePatient } from '@/hooks/queries';
import { toast } from '@/components/ui/toaster';
import { ANALYTICS_EVENTS, track } from '@/lib/analytics';
import { ClinicCombobox } from '@/components/ClinicCombobox';
import type { CreatePatientInput } from '@/api/patients';

/**
 * Lightweight "add patient to a clinic" flow — the deliberate opposite of
 * NewPatientDialog. It collects only identity + clinic and creates the patient
 * with NO initial logging period (`subscription_name: 'none'`), so every other
 * setting falls back to the clinic's defaults server-side.
 *
 * Use when a clinician just wants a patient on the roster and will set up a
 * logging trial later (or never). The full dialog remains for the complete
 * enrolment-with-trial flow.
 *
 * Field order (clinic first) mirrors the full dialog so the two feel like the
 * same family; clinic sits first because it's the one thing that can't default.
 */
const schema = z.object({
  clinic_id: z.coerce.number().int().positive('Choose a clinic'),
  first_name: z.string().trim().min(1, 'First name is required'),
  last_name: z.string().trim().min(1, 'Last name is required'),
  email: z.string().trim().email('Enter a valid email'),
  phone_number: z.string().optional(),
});

type Props = {
  open: boolean;
  clinicianId: number;
  onClose: () => void;
  onCreated?: (patientId: number) => void;
};

export const QuickAddPatientDialog = ({
  open,
  clinicianId,
  onClose,
  onCreated,
}: Props) => {
  const { t } = useTranslation();
  const clinics = useClinicianClinics(clinicianId);
  const createPatient = useCreatePatient(clinicianId);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [clinicId, setClinicId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  // Reset the form whenever it closes so the next open is fresh.
  useEffect(() => {
    if (open) return;
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setClinicId('');
    setError(null);
  }, [open]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const parsed = schema.safeParse({
      first_name: firstName,
      last_name: lastName,
      email,
      phone_number: phone || undefined,
      clinic_id: clinicId,
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t('Please review the form.'));
      return;
    }
    // No subscription block: `subscription_name: 'none'` tells the server to
    // create the patient with no logging period and inherit every clinic
    // default. This is the whole point of the quick-add flow.
    const payload: CreatePatientInput = {
      first_name: parsed.data.first_name,
      last_name: parsed.data.last_name,
      email: parsed.data.email,
      phone_number: parsed.data.phone_number || null,
      clinic_id: parsed.data.clinic_id,
      subscription_name: 'none',
    };
    try {
      const result = await createPatient.mutateAsync(payload);
      toast({
        title: t('Patient added'),
        description: `${parsed.data.first_name} ${parsed.data.last_name}`,
      });
      track(ANALYTICS_EVENTS.PATIENT_ADDED, {
        quick_add: true,
        has_phone: !!parsed.data.phone_number,
      });
      onClose();
      const newId =
        (result as { patient_id?: number; id?: number }).patient_id ??
        (result as { id?: number }).id;
      if (newId && onCreated) onCreated(newId);
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
        t('Could not create patient. Check the form and try again.');
      setError(msg);
      toast({ title: t('Could not create patient'), variant: 'destructive', description: msg });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] w-[min(96vw,460px)] max-w-[96vw] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('Quick add to clinic')}</DialogTitle>
          <DialogDescription>
            {t('Adds the patient with no logging period, using the clinic defaults. You can start a logging trial later.')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <div className="space-y-1">
            <Label>{t('Clinic')}</Label>
            {clinics.isLoading ? (
              <Skeleton className="h-10 w-full" />
            ) : (
              <ClinicCombobox
                clinics={clinics.data ?? []}
                value={clinicId ? Number(clinicId) : null}
                onChange={(id) => setClinicId(String(id))}
                placeholder={t('Select a clinic')}
                autoFocus
              />
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="qa-first">{t('First name')}</Label>
              <Input id="qa-first" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
            </div>
            <div className="space-y-1">
              <Label htmlFor="qa-last">{t('Last name')}</Label>
              <Input id="qa-last" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="qa-email">{t('Email')}</Label>
            <Input id="qa-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>

          <div className="space-y-1">
            <Label htmlFor="qa-phone">{t('Phone (optional)')}</Label>
            <Input id="qa-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>

          {error && <p className="text-sm text-destructive" role="alert">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={createPatient.isPending}>
              {t('Cancel')}
            </Button>
            <Button type="submit" disabled={createPatient.isPending}>
              {createPatient.isPending ? t('Adding…') : t('Add to clinic')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
